import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import {
  BottomNavigation,
  BottomNavigationAction,
  Paper,
  Fab,
  Badge,
  Box,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
} from '@mui/material';
import {
  CalendarToday as CalendarIcon,
  Schedule as ScheduleIcon,
  People as PeopleIcon,
  Article as NewsIcon,
  Assessment as ReportsIcon,
  Settings as SettingsIcon,
  Add as AddIcon,
  FitnessCenter as BalanceIcon,
  Person as PersonIcon,
  Payments as PaymentsIcon,
  MailOutline as MailIcon,
  Lock as LockIcon,
  MoreHoriz as MoreIcon,
  ChevronLeft as ChevronLeftIcon,
  ChevronRight as ChevronRightIcon,
} from '@mui/icons-material';
import { useResponsive } from '../hooks/useResponsive';
import { TabDef, TabKey, mobileNavItems, mobileMoreItems } from './dashboardTabs';
import { useToastObstacle } from '../contexts/FeedbackContext';

interface MobileBottomNavProps {
  activeTab: number;
  onTabChange: (newValue: number) => void;
  /** A Dashboard fül-listája (dashboardTabs.buildTabs) — az alsó sáv EBBŐL épül, nem külön listából. */
  tabs: TabDef[];
  isCoach?: boolean;
  onFabClick?: () => void;
  profileIncomplete?: boolean;
  /** Hiányzó profiladat felsorolás — a Profil-elem címkéje/akadálymentes neve [Web regisztráció születési dátum, 2026-10-04]. */
  profileMissingText?: string;
  /**
   * [Web UX 4. csomag (Admin teendők), 2026-10-04] A „Beállítások" elem jelvénye: a függő regisztrációk + asztalfoglalások +
   * magánóra-igénylések ÖSSZEGE (a Dashboard `usePendingAdminCounts` közös forrásából). 0 / hiányzó → nincs jelvény.
   */
  settingsPendingCount?: number;
}

function iconFor(
  key: TabKey,
  isCoach: boolean | undefined,
  profileIncomplete: boolean | undefined,
  settingsPendingCount: number | undefined,
): React.ReactElement {
  switch (key) {
    case 'calendar': return <CalendarIcon />;
    case 'profile': return profileIncomplete ? <Badge color="error" variant="dot"><PersonIcon /></Badge> : <PersonIcon />;
    case 'stats': return isCoach ? <ReportsIcon /> : <BalanceIcon />;
    case 'availability': return <ScheduleIcon />;
    case 'attendance': return <PeopleIcon />;
    case 'news': return <NewsIcon />;
    case 'reports': return <ReportsIcon />;
    // A jelvény az IKONON ül (mint a Profilnál), a szám aria-hidden: az akadálymentes szöveg a `title`-ben van, az `aria-label` VÁLTOZATLAN
    // („Beállítások tab" — az E2E `nav.ts` erre épít).
    case 'settings':
      return settingsPendingCount && settingsPendingCount > 0 ? (
        <Badge badgeContent={settingsPendingCount} color="error" max={99} slotProps={{ badge: { 'aria-hidden': true, 'data-testid': 'mobile-settings-pending-badge' } as React.HTMLAttributes<HTMLSpanElement> }}>
          <SettingsIcon />
        </Badge>
      ) : <SettingsIcon />;
    case 'sos': return <LockIcon />;
    case 'coachPayments':
    case 'myPayments': return <PaymentsIcon />;
    case 'messages': return <MailIcon />;
    default: return <CalendarIcon />;
  }
}

/**
 * A „Továbbiak" elem értéke a `BottomNavigation`-ben. Negatív, hogy egyetlen valódi fül-indexszel
 * se ütközhessen (a fül-indexek >= 0). [Web UX 9 / T4, 2026-10-13]
 */
export const MORE_VALUE = -100;

/** Jelezze-e a szél, hogy arra rejtett tartalom van (1 px tűrés a tört pixelek miatt). */
function overflowState(el: HTMLElement): { left: boolean; right: boolean } {
  return {
    left: el.scrollLeft > 1,
    right: el.scrollLeft + el.clientWidth < el.scrollWidth - 1,
  };
}

/**
 * Mobil (<600 px) alsó navigáció. A menüpontok a közös fül-listából jönnek (`dashboardTabs`), így
 * az asztali fülek és az alsó sáv nem csúszhatnak szét — a „Hírek" pl. eddig az edző/játékos
 * sávjából hiányzott, mert külön listában épült. [Web UX A2/A5, 2026-09-12]
 */
const MobileBottomNav: React.FC<MobileBottomNavProps> = ({
  activeTab,
  onTabChange,
  tabs,
  isCoach,
  onFabClick,
  profileIncomplete,
  profileMissingText,
  settingsPendingCount,
}) => {
  const { isMobile } = useResponsive();
  // [Web UX 9 / T3, 2026-10-13] A menü a toast „akadálya": a visszajelzés a menü FÖLÉ kerül, nem
  // takarja (2. körös UX-lista (`Documents/WEB_UX_JAVASLATOK_2KOR_2026-10-04.md`) 5.5; a toast eddig fixen `bottom: 8`-nál ült, a menü területén).
  // A hook a korai `return` ELŐTT áll (a hookok sorrendje nem függhet a szélességtől).
  const toastObstacleRef = useToastObstacle();

  // [Web UX 9 / T4, 2026-10-13] A hookok a korai `return` ELŐTT (a sorrend nem függhet a szélességtől).
  const navItems = mobileNavItems(tabs);
  const moreItems = mobileMoreItems(tabs);
  const activeInMore = moreItems.some((m) => m.value === activeTab);
  const barRef = useRef<HTMLDivElement | null>(null);
  const [moreAnchor, setMoreAnchor] = useState<HTMLElement | null>(null);
  const [edges, setEdges] = useState<{ left: boolean; right: boolean }>({ left: false, right: false });

  const updateEdges = useCallback(() => {
    const el = barRef.current;
    if (!el) return;
    const next = overflowState(el);
    setEdges((prev) => (prev.left === next.left && prev.right === next.right ? prev : next));
  }, []);

  /*
   * [Web UX 9 / T4, 2026-10-13] A KIVÁLASZTOTT ELEM A NÉZETBE. MI VOLT A BAJ (2. körös UX-lista 2.1,
   * `MobileBottomNav.tsx:~94-99` a javítás előtt): a `?ful=` miatt F5 és közvetlen link után a Dashboard
   * visszaállítja a fület, de a vízszintesen görgethető sáv a bal szélén maradt — a kijelölt elem
   * (pl. a „Kifizetések") a képen kívül lehetett, semmi nem látszott kiemeltnek. Most betöltéskor ÉS
   * váltáskor az aktív elem (vagy, ha a fül a „Továbbiak" alatt van, a „Továbbiak") középre görgetődik.
   * `block: 'nearest'`: függőlegesen nem mozdítja a lapot. A jsdom-ban nincs `scrollIntoView` → védve.
   */
  useLayoutEffect(() => {
    const el = barRef.current;
    if (!el) return;
    const selected = el.querySelector<HTMLElement>('.MuiBottomNavigationAction-root.Mui-selected');
    if (selected && typeof selected.scrollIntoView === 'function') {
      selected.scrollIntoView({ inline: 'center', block: 'nearest' });
    }
    updateEdges();
  }, [activeTab, activeInMore, navItems.length, isMobile, updateEdges]);

  // A „van tovább" jelzés a szélesség változására (forgatás, ablakméret) is frissül.
  useEffect(() => {
    if (!isMobile) return undefined;
    window.addEventListener('resize', updateEdges);
    let ro: ResizeObserver | undefined;
    if (typeof ResizeObserver !== 'undefined' && barRef.current) {
      ro = new ResizeObserver(() => updateEdges());
      ro.observe(barRef.current);
    }
    return () => {
      window.removeEventListener('resize', updateEdges);
      ro?.disconnect();
    };
  }, [isMobile, updateEdges]);

  /*
   * [Web UX 9 / T4, 2026-10-13, validátor M1] A NYITOTT „Továbbiak" MENÜ ZÁRUL, HA A SÁV ELTŰNIK.
   * MI VOLT A BAJ (`MobileBottomNav.tsx:122` állapot, `:165` korai `return`, `:324-328` Menu): nyitott
   * menü mellett 390 → 900 → 390 px átméretezéskor (telefon-forgatás) a `moreAnchor` a már lecsatolt
   * régi elemre mutatott, a menü magától újranyílt a bal felső sarokban, és MUI-hiba jött („The
   * `anchorEl` prop provided to the component is invalid"). Most a sáv eltűnésekor az állapot nullázódik.
   * A hook a korai `return` ELŐTT áll (a hookok sorrendje nem függhet a szélességtől).
   */
  useEffect(() => {
    if (!isMobile) setMoreAnchor(null);
  }, [isMobile]);

  if (!isMobile) return null;

  /** A menüből választás a sáv elemével AZONOS úton megy (Dashboard `handleMobileTabChange`: mentetlen-kapu + `?ful=`). */
  const chooseFromMore = (value: number) => {
    setMoreAnchor(null);
    onTabChange(value);
  };
  // Az admin rejtett Adatlapjának „hiányos profil" pöttye ne vesszen el a menü mögött.
  const moreHasProfileDot = !!profileIncomplete && moreItems.some((m) => m.key === 'profile');
  const fadeSx = {
    position: 'absolute' as const,
    top: 0,
    bottom: 0,
    width: 28,
    // z-index 0: a felirat/ikon FÖLÉ kerül, de a MUI jelvény (`.MuiBadge-badge`, z-index 1) alatt marad —
    // a jelzés nem takarhatja a teendő-jelvényt.
    zIndex: 0,
    pointerEvents: 'none' as const,
    display: 'flex',
    alignItems: 'center',
    color: 'text.secondary',
  };

  return (
    <>
      <Paper
        ref={toastObstacleRef}
        data-testid="mobile-bottom-nav"
        sx={{
          flexShrink: 0,
          position: 'relative',
          zIndex: 1000,
        }}
        elevation={3}
      >
        <BottomNavigation
          ref={barRef}
          // [Web UX 9 / T4, 2026-10-13] Ha az aktív fül a „Továbbiak" alatt van, a „Továbbiak" elem a kiemelt.
          value={activeInMore ? MORE_VALUE : activeTab}
          onChange={(event, newValue) => {
            // A „Továbbiak" a saját `onClick`-jében nyitja a menüt — az nem fülváltás.
            if (newValue === MORE_VALUE) return;
            onTabChange(newValue);
          }}
          onScroll={updateEdges}
          showLabels
          data-testid="mobile-bottom-nav-bar"
          sx={{
            height: 'auto',
            minHeight: 64,
            paddingBottom: 'env(safe-area-inset-bottom, 0px)',
            // Adminként nyolc menüpont van, ami egy 360 pontos telefonon NEM fér ki:
            // a BottomNavigation alapból egymásra préseli az elemeket, és a feliratok
            // levágódnak, illetve egymásra csúsznak („KimutatásoBeállításoKifizetés").
            // Ezért a sáv vízszintesen görgethető, az elemek pedig nem zsugorodnak.
            justifyContent: 'flex-start',
            overflowX: 'auto',
            overflowY: 'hidden',
            // A görgetősáv elvenne a amúgy is szűk magasságból.
            scrollbarWidth: 'none',
            '&::-webkit-scrollbar': { display: 'none' },
            // [Web UX 9 / T4, 2026-10-13] Az elemek a TARTALMUK szélességéből indulnak és kitöltik a
            // maradékot (`1 0 auto`), nem zsugorodnak; 4 px oldalsó belső margó, 48 px minimum (érintési
            // célméret). Eddig 72 px minimum + 12 px margó: az admin sávja 603 px volt 360 px-en (MÉRVE).
            '& .MuiBottomNavigationAction-root': {
              flex: '1 0 auto',
              minWidth: 48,
              maxWidth: 'none',
              minHeight: 48,
              fontSize: '0.75rem',
              padding: '4px 4px 6px',
              '&.Mui-selected': {
                paddingTop: 2,
                paddingBottom: 8,
              },
            },
            '& .MuiBottomNavigationAction-icon': {
              fontSize: 20,
              marginBottom: 1,
            },
            '& .MuiBottomNavigationAction-label': {
              fontSize: '0.7rem',
              lineHeight: 1,
              whiteSpace: 'nowrap',
              '&.Mui-selected': {
                fontSize: '0.7rem',
              },
            },
          }}
        >
          {navItems.map((item) => (
            <BottomNavigationAction
              key={item.key}
              label={item.mobileLabel}
              icon={iconFor(item.key, isCoach, profileIncomplete, settingsPendingCount)}
              value={item.value}
              data-testid={`mobile-nav-${item.mobileLabel.toLowerCase()}`}
              aria-label={`${item.mobileLabel} tab`}
              title={
                item.key === 'profile' && profileIncomplete
                  ? profileMissingText
                  : item.key === 'settings' && settingsPendingCount && settingsPendingCount > 0
                    ? `${settingsPendingCount} teendő vár`
                    : undefined
              }
              sx={{
                '&:focus': {
                  outline: '2px solid',
                  outlineColor: 'primary.main',
                  outlineOffset: 2,
                },
              }}
            />
          ))}
          {moreItems.length > 0 && (
            <BottomNavigationAction
              key="__more"
              label="Továbbiak"
              value={MORE_VALUE}
              icon={moreHasProfileDot ? <Badge color="error" variant="dot"><MoreIcon /></Badge> : <MoreIcon />}
              data-testid="mobile-nav-továbbiak"
              aria-label="Továbbiak tab"
              aria-haspopup="menu"
              aria-expanded={moreAnchor ? 'true' : 'false'}
              aria-controls={moreAnchor ? 'mobile-nav-more-menu' : undefined}
              // [Web UX 9 / T4, 2026-10-13, validátor M2] A pötty mellé a hiány FELSOROLÁSA is (eddig az admin
              // Adatlap-elemének `title`-je volt, a menübe kerüléssel elveszett). Az `aria-label` SZÁNDÉKOSAN
              // változatlan (dosszié 5.22: E2E-szelektor `<Név> tab`); a `title` akadálymentes leírásként olvasható.
              title={[
                activeInMore ? `Most: ${moreItems.find((m) => m.value === activeTab)?.mobileLabel}` : '',
                moreHasProfileDot ? profileMissingText ?? '' : '',
              ].filter(Boolean).join(' · ') || undefined}
              onClick={(e: React.MouseEvent<HTMLElement>) => setMoreAnchor(e.currentTarget)}
              sx={{
                '&:focus': {
                  outline: '2px solid',
                  outlineColor: 'primary.main',
                  outlineOffset: 2,
                },
              }}
            />
          )}
        </BottomNavigation>
        {/* [Web UX 9 / T4, 2026-10-13] „Van tovább" jelzés: halványuló szél + nyíl, CSAK ha arra rejtett elem van. */}
        {edges.left && (
          <Box
            data-testid="mobile-nav-fade-left"
            aria-hidden
            sx={{ ...fadeSx, left: 0, justifyContent: 'flex-start', background: (t) => `linear-gradient(to right, ${t.palette.background.paper}, transparent)` }}
          >
            <ChevronLeftIcon fontSize="small" />
          </Box>
        )}
        {edges.right && (
          <Box
            data-testid="mobile-nav-fade-right"
            aria-hidden
            sx={{ ...fadeSx, right: 0, justifyContent: 'flex-end', background: (t) => `linear-gradient(to left, ${t.palette.background.paper}, transparent)` }}
          >
            <ChevronRightIcon fontSize="small" />
          </Box>
        )}
      </Paper>

      {moreItems.length > 0 && (
        <Menu
          id="mobile-nav-more-menu"
          data-testid="mobile-nav-more-menu"
          anchorEl={moreAnchor}
          open={Boolean(moreAnchor)}
          onClose={() => setMoreAnchor(null)}
          anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
          transformOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        >
          {moreItems.map((item) => {
            // [Web UX 9 / T4, 2026-10-13, validátor M2] A menübeli Adatlap-pont a hiányos profil FELSOROLÁSÁT is
            // mutatja (látható másodlagos sor + `title`) — érintőképernyőn a `title` nem jelenik meg, a pötty
            // magában nem mondja meg, MI hiányzik; a képernyőolvasó a menüpont nevében olvassa fel.
            const missing = item.key === 'profile' && profileIncomplete && profileMissingText ? profileMissingText : undefined;
            return (
              <MenuItem
                key={item.key}
                selected={item.value === activeTab}
                aria-current={item.value === activeTab ? 'page' : undefined}
                data-testid={`mobile-nav-more-${item.key}`}
                title={missing}
                onClick={() => chooseFromMore(item.value)}
              >
                <ListItemIcon>{iconFor(item.key, isCoach, profileIncomplete, settingsPendingCount)}</ListItemIcon>
                <ListItemText
                  primary={item.mobileLabel}
                  secondary={missing}
                  // [Web UX 9 / T4, 2026-10-13, 2. validátor F1] A MenuItem `white-space: nowrap`-ja miatt a hosszú
                  // felsorolás 320/360 px-en szó közben levágódott (a menü papírja 288/328 px) — a sor tördelhető.
                  slotProps={missing ? { secondary: { 'data-testid': 'mobile-nav-more-profile-missing', style: { whiteSpace: 'normal' } } as React.HTMLAttributes<HTMLElement> } : undefined}
                />
              </MenuItem>
            );
          })}
        </Menu>
      )}

      {/* Floating Action Button */}
      {onFabClick && (
        <Fab
          color="primary"
          aria-label="Új esemény hozzáadása"
          data-testid="add-event-fab"
          sx={{
            position: 'absolute',
            top: -70,
            right: 16,
            zIndex: 1001,
            minHeight: 56,
            minWidth: 56,
            '&:focus': {
              outline: '3px solid',
              outlineColor: 'secondary.main',
              outlineOffset: 2,
            },
          }}
          onClick={onFabClick}
        >
          <AddIcon />
        </Fab>
      )}
    </>
  );
};

export default MobileBottomNav;
